import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { IconArrowRight, IconLock, IconMail } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
  Anchor,
  Box,
  Button,
  Group,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Transition,
  useMantineColorScheme,
} from '@mantine/core'
import TileLayer from 'ol/layer/Tile'
import Map from 'ol/Map'
import { fromLonLat } from 'ol/proj'
import XYZ from 'ol/source/XYZ'
import View from 'ol/View'

type AgLoginFormProps = {
  onSubmit: (email: string, password: string) => Promise<void> | void
  loading?: boolean
  error?: string
}

// Spain coordinates for random starting points
const SPAIN_LOCATIONS = [
  { center: [-3.7038, 40.4168], name: 'Madrid' },
  { center: [2.1734, 41.3851], name: 'Barcelona' },
  { center: [-5.9845, 37.3891], name: 'Sevilla' },
  { center: [-0.3763, 39.4699], name: 'Valencia' },
  { center: [-2.9349, 43.263], name: 'Bilbao' },
]

export default function AgLoginForm({ onSubmit, loading = false, error }: AgLoginFormProps) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [mounted, setMounted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const { t } = useTranslation()
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<Map | null>(null)

  // Force dark mode for login page
  const { setColorScheme } = useMantineColorScheme()
  useLayoutEffect(() => {
    setColorScheme('dark')
  }, [setColorScheme])

  // Logo URL
  const logoUrl = `${(import.meta.env.VITE_API_BASE_URL || '').replace(/\/+$/, '')}/static/2.svg`

  // Initialize background map
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) {
      return
    }

    const randomLocation = SPAIN_LOCATIONS[Math.floor(Math.random() * SPAIN_LOCATIONS.length)]

    const map = new Map({
      target: mapRef.current,
      layers: [
        new TileLayer({
          source: new XYZ({
            url: 'https://tms-pnoa-ma.idee.es/1.0.0/pnoa-ma/{z}/{x}/{-y}.jpeg',
            maxZoom: 19,
          }),
        }),
      ],
      view: new View({
        center: fromLonLat(randomLocation.center),
        zoom: 17,
      }),
      controls: [],
      interactions: [],
    })

    mapInstanceRef.current = map

    // Slow pan animation
    const view = map.getView()
    let angle = 0
    const animate = () => {
      angle += 0.001
      const currentCenter = view.getCenter()
      if (currentCenter) {
        view.setCenter([
          currentCenter[0] + Math.sin(angle) * 2,
          currentCenter[1] + Math.cos(angle) * 1,
        ])
      }
      requestAnimationFrame(animate)
    }
    animate()

    return () => {
      map.setTarget(undefined)
      mapInstanceRef.current = null
    }
  }, [])

  // Mount animation
  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 100)
    return () => clearTimeout(timer)
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      await onSubmit(email, password)
    } finally {
      setSubmitting(false)
    }
  }

  const isLoading = loading || submitting

  return (
    <Box
      style={{
        height: '100vh',
        width: '100vw',
        position: 'fixed',
        top: 0,
        left: 0,
        overflow: 'hidden',
      }}
    >
      {/* Background map */}
      <Box
        ref={mapRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
        }}
      />

      {/* Dark overlay */}
      <Box
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0.7) 100%)',
          zIndex: 1,
        }}
      />

      {/* Animated gradient accent */}
      <Box
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            'radial-gradient(ellipse at 30% 20%, rgba(0, 255, 0, 0.1) 0%, transparent 50%)',
          zIndex: 2,
          pointerEvents: 'none',
        }}
      />

      {/* Content */}
      <Box
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 20,
          overflowY: 'auto',
        }}
      >
        <Transition mounted={mounted} transition="slide-up" duration={600}>
          {(styles) => (
            <Paper
              shadow="xl"
              p="xl"
              radius="lg"
              style={{
                ...styles,
                width: '100%',
                maxWidth: 420,
                background: 'rgba(0, 0, 0, 0.75)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(0, 255, 0, 0.2)',
              }}
            >
              <Stack gap="lg">
                {/* Logo */}
                <Group justify="center" mb="md">
                  <img
                    src={logoUrl}
                    alt="Asbestos.ai"
                    style={{
                      height: 60,
                      filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
                    }}
                  />
                </Group>

                {/* Welcome text */}
                <Box ta="center" mb="sm">
                  <Text
                    size="xl"
                    fw={700}
                    style={{
                      background: 'linear-gradient(135deg, #00ff00 0%, #00cc00 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    {t('login.welcome', 'Bienvenido')}
                  </Text>
                  <Text size="sm" c="rgba(255,255,255,0.6)" mt={4}>
                    {t('login.subtitle', 'Inicia sesión para continuar')}
                  </Text>
                </Box>

                {/* Form */}
                <form onSubmit={handleSubmit}>
                  <Stack gap="md">
                    <TextInput
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      label={t('login.email', 'Correo electrónico')}
                      placeholder={t('login.emailPlaceholder', 'tu@email.com')}
                      type="email"
                      autoComplete="email"
                      leftSection={<IconMail size={18} color="rgba(0,255,0,0.7)" />}
                      size="md"
                      styles={{
                        label: { color: 'rgba(255,255,255,0.8)', marginBottom: 6 },
                        input: {
                          background: 'rgba(255,255,255,0.05)',
                          border: '1px solid rgba(0, 255, 0, 0.2)',
                          color: '#fff',
                          '&::placeholder': { color: 'rgba(255,255,255,0.3)' },
                          '&:focus': {
                            borderColor: '#00ff00',
                            boxShadow: '0 0 0 2px rgba(0, 255, 0, 0.1)',
                          },
                        },
                      }}
                    />

                    <PasswordInput
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      label={t('login.password', 'Contraseña')}
                      placeholder={t('login.passwordPlaceholder', '••••••••')}
                      autoComplete="current-password"
                      leftSection={<IconLock size={18} color="rgba(0,255,0,0.7)" />}
                      size="md"
                      styles={{
                        label: { color: 'rgba(255,255,255,0.8)', marginBottom: 6 },
                        input: {
                          background: 'rgba(255,255,255,0.05)',
                          border: '1px solid rgba(0, 255, 0, 0.2)',
                          color: '#fff',
                          '&::placeholder': { color: 'rgba(255,255,255,0.3)' },
                          '&:focus': {
                            borderColor: '#00ff00',
                            boxShadow: '0 0 0 2px rgba(0, 255, 0, 0.1)',
                          },
                        },
                        innerInput: {
                          color: '#fff',
                        },
                        visibilityToggle: {
                          color: 'rgba(255,255,255,0.5)',
                          '&:hover': { color: '#00ff00' },
                        },
                      }}
                    />

                    <Group justify="flex-end">
                      <Anchor
                        component={Link}
                        to="/forgot-password"
                        size="sm"
                        style={{
                          color: 'rgba(255,255,255,0.7)',
                          textDecoration: 'none',
                          transition: 'color 0.2s',
                        }}
                      >
                        {t('login.forgotPassword', '¿Olvidaste tu contraseña?')}
                      </Anchor>
                    </Group>

                    {error && (
                      <Paper
                        p="xs"
                        radius="sm"
                        style={{
                          background: 'rgba(255, 0, 0, 0.1)',
                          border: '1px solid rgba(255, 0, 0, 0.3)',
                        }}
                      >
                        <Text size="sm" c="#ff6b6b" ta="center">
                          {t(error)}
                        </Text>
                      </Paper>
                    )}

                    <Button
                      type="submit"
                      fullWidth
                      size="md"
                      loading={isLoading}
                      rightSection={!isLoading && <IconArrowRight size={18} />}
                      styles={{
                        root: {
                          marginTop: 8,
                          background: 'linear-gradient(135deg, #00ff00 0%, #00cc00 100%)',
                          border: 'none',
                          color: '#000',
                          fontWeight: 600,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #00cc00 0%, #009900 100%)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 20px rgba(0, 255, 0, 0.3)',
                          },
                          '&:active': {
                            transform: 'translateY(0)',
                          },
                        },
                      }}
                    >
                      {isLoading
                        ? t('login.loading', 'Cargando...')
                        : t('login.button', 'Iniciar sesión')}
                    </Button>
                  </Stack>
                </form>

                {/* Back to map link */}
                <Group justify="center" mt="sm">
                  <Anchor
                    href="/"
                    size="sm"
                    style={{
                      color: 'rgba(255,255,255,0.7)',
                      textDecoration: 'none',
                      transition: 'color 0.2s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = '#00ff00')}
                    onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.7)')}
                  >
                    {t('login.backToMap', '← Volver al mapa público')}
                  </Anchor>
                </Group>
              </Stack>
            </Paper>
          )}
        </Transition>
      </Box>

      {/* Footer */}
      <Box
        style={{
          position: 'absolute',
          bottom: 16,
          left: 0,
          right: 0,
          textAlign: 'center',
          zIndex: 3,
        }}
      >
        <Text size="xs" c="rgba(255,255,255,0.3)">
          © {new Date().getFullYear()} Asbestos.ai —{' '}
          {t('login.footer', 'Plataforma de gestión de amianto')}
        </Text>
      </Box>
    </Box>
  )
}
